import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Phone, Mail, ExternalLink, Download, Video, Sparkles, Crown, Star, Clock, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEMM_SITE = 'https://wemillionairesmm.com';

const TIER_META = {
  founder: { label: 'Founder', icon: Crown, accent: 'from-amber-500/40 via-yellow-500/20 to-transparent', badge: 'bg-amber-500 text-black' },
  growth: { label: 'Growth', icon: Sparkles, accent: 'from-emerald-500/40 via-emerald-500/10 to-transparent', badge: 'bg-emerald-500 text-black' },
  ascend: { label: 'Ascend', icon: Star, accent: 'from-blue-500/40 via-blue-500/10 to-transparent', badge: 'bg-blue-500 text-white' },
};

/**
 * PublicCard — the standalone `/card/:userId` page.
 *
 * The WeMillionairesMM CTA behaves differently depending on the member's
 * state (so the button never leads to a 404 anymore):
 *   - `wemm_profile_url` set → CTA opens THAT URL and the QR code encodes
 *     it too — one scan and you land on their WeMillionairesMM profile.
 *   - Founder without a URL yet → "Account being provisioned — check your
 *     email" block with a link to WeMillionairesMM home. QR falls back to
 *     the card URL.
 *   - Non-founder → "Become a WeMillionairesMM member" invite CTA linking
 *     to the sister site's home page (they can sign up there). QR encodes
 *     the card URL.
 */
export default function PublicCard() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/members/${userId}/profile`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? 'Card not found' : 'Could not load card');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const u = data?.user;
  const tier = (u?.membership_tier || '').toLowerCase();
  const isFounder = tier === 'founder';
  const meta = TIER_META[tier] || { label: 'Member', icon: Star, accent: 'from-white/20 to-transparent', badge: 'bg-white/10 text-white' };
  const TierIcon = meta.icon;

  const introVideoUrl = u?.microsite_intro_video_url
    ? (u.microsite_intro_video_url.startsWith('http') ? u.microsite_intro_video_url : `${API_URL}${u.microsite_intro_video_url}`)
    : '';

  const phone = u?.contact_phone || u?.phone || '';
  const email = u?.contact_email || '';
  const wemmProfileUrl = (u?.wemm_profile_url || '').trim();

  const cardShareUrl = typeof window !== 'undefined' ? `${window.location.origin}/card/${userId}` : '';

  // The QR code should encode the WeMillionairesMM profile when the
  // member has provided one — this makes physical business cards / phone
  // scans go straight to their real profile. Falls back to the internal
  // card URL when no WMM link is set.
  const qrTarget = wemmProfileUrl || cardShareUrl;

  const wemmCta = useMemo(() => {
    if (wemmProfileUrl) {
      return {
        href: wemmProfileUrl,
        label: 'View on WeMillionairesMM',
        sublabel: 'Full profile',
        icon: ExternalLink,
        tone: 'gold',
      };
    }
    if (isFounder) {
      return {
        href: WEMM_SITE,
        label: 'Account being provisioned',
        sublabel: 'Check your email for access — or sign in with the same email at wemillionairesmm.com',
        icon: Clock,
        tone: 'pending',
      };
    }
    return {
      href: WEMM_SITE,
      label: 'Join WeMillionairesMM',
      sublabel: 'Create your account to unlock the full network',
      icon: UserPlus,
      tone: 'invite',
    };
  }, [wemmProfileUrl, isFounder]);

  const ctaClasses = {
    gold: 'bg-brand-gold/15 border-brand-gold/40 hover:bg-brand-gold/25',
    pending: 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20',
    invite: 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20',
  }[wemmCta.tone];
  const ctaIconBg = {
    gold: 'bg-brand-gold/30 text-brand-gold',
    pending: 'bg-amber-500/25 text-amber-300',
    invite: 'bg-blue-500/25 text-blue-300',
  }[wemmCta.tone];
  const ctaLabelClr = {
    gold: 'text-brand-gold',
    pending: 'text-amber-300',
    invite: 'text-blue-300',
  }[wemmCta.tone];
  const CtaIcon = wemmCta.icon;

  const downloadVCard = () => {
    if (!u) return;
    // Minimal vCard 3.0 — enough for iOS/Android/Outlook to import cleanly.
    const parts = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${u.name || 'WeMasterminds Member'}`,
      `TITLE:${(u.specialty || '').replace(/\r?\n/g, ' ')}`,
      'ORG:WeMasterminds',
    ];
    if (email) parts.push(`EMAIL;TYPE=INTERNET:${email}`);
    if (phone) parts.push(`TEL;TYPE=CELL:${phone}`);
    if (cardShareUrl) parts.push(`URL:${cardShareUrl}`);
    if (wemmProfileUrl) parts.push(`URL;TYPE=WORK:${wemmProfileUrl}`);
    parts.push('END:VCARD');
    const blob = new Blob([parts.join('\r\n')], { type: 'text/vcard;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(u.name || 'contact').replace(/\s+/g, '_')}.vcf`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/60">
        Loading card…
      </div>
    );
  }

  if (error || !u) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white text-center px-6" data-testid="card-error">
        <div>
          <p className="text-2xl font-playfair mb-2">Card unavailable</p>
          <p className="text-white/60 mb-6">{error || 'This member does not have a public card yet.'}</p>
          <Link to="/" className="text-brand-gold underline">Return to WeMasterminds</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-neutral-950 to-black text-white" data-testid="public-card-page">
      <div className={`relative bg-gradient-to-b ${meta.accent}`}>
        {u.microsite_cover_url ? (
          <img src={u.microsite_cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        ) : null}
        <div className="relative max-w-md mx-auto px-6 pt-16 pb-8 flex flex-col items-center text-center">
          <div className="relative">
            <div className="w-36 h-36 rounded-full ring-4 ring-black/40 overflow-hidden bg-neutral-800 shadow-2xl">
              {u.profile_photo ? (
                <img src={u.profile_photo} alt={u.name} className="w-full h-full object-cover" data-testid="card-avatar" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl font-playfair text-white/50">
                  {u.name?.[0]?.toUpperCase() || 'W'}
                </div>
              )}
            </div>
            <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest shadow-lg ${meta.badge}`} data-testid="card-tier-badge">
              <TierIcon className="w-3 h-3" /> {meta.label}
            </span>
          </div>
          <h1 className="mt-6 text-3xl font-playfair" data-testid="card-name">{u.name}</h1>
          {u.microsite_tagline && (
            <p className="mt-1 text-sm text-white/70 italic max-w-xs">{u.microsite_tagline}</p>
          )}
          {u.specialty && (
            <p className="mt-2 text-xs uppercase tracking-[0.25em] text-brand-gold">{u.specialty}</p>
          )}
        </div>
      </div>

      {introVideoUrl && (
        <div className="max-w-md mx-auto px-6 pb-4">
          <div className="rounded-2xl overflow-hidden bg-neutral-900 border border-white/5">
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-white/60">
              <Video className="w-3.5 h-3.5 text-brand-gold" /> Intro from {u.name?.split(' ')[0]}
            </div>
            <video
              src={introVideoUrl}
              controls
              playsInline
              className="w-full aspect-video bg-black"
              data-testid="card-intro-video"
            />
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto px-6 py-4 space-y-3">
        {phone && (
          <a
            href={`tel:${phone.replace(/[^\d+]/g, '')}`}
            className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition"
            data-testid="card-call-btn"
          >
            <div className="w-10 h-10 rounded-full bg-emerald-500/25 flex items-center justify-center">
              <Phone className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-emerald-400">Tap to Call</p>
              <p className="text-sm text-white">{phone}</p>
            </div>
          </a>
        )}
        {email && (
          <a
            href={`mailto:${email}`}
            className="flex items-center gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition"
            data-testid="card-email-btn"
          >
            <div className="w-10 h-10 rounded-full bg-blue-500/25 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-blue-400">Tap to Email</p>
              <p className="text-sm text-white truncate">{email}</p>
            </div>
          </a>
        )}
        <button
          onClick={downloadVCard}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition"
          data-testid="card-save-vcard-btn"
        >
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-xs uppercase tracking-wider text-white/70">Save to Contacts</p>
            <p className="text-sm text-white">Download vCard (.vcf)</p>
          </div>
        </button>
        <a
          href={wemmCta.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-3 p-4 rounded-2xl border transition ${ctaClasses}`}
          data-testid="card-wemm-btn"
          data-wemm-state={wemmCta.tone}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ctaIconBg}`}>
            <CtaIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-xs uppercase tracking-wider ${ctaLabelClr}`}>{wemmCta.label}</p>
            <p className="text-xs text-white/80 leading-snug">{wemmCta.sublabel}</p>
          </div>
        </a>
      </div>

      {qrTarget && (
        <div className="max-w-md mx-auto px-6 py-6 flex flex-col items-center border-t border-white/5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3" data-testid="card-qr-label">
            {wemmProfileUrl ? 'Scan for WeMillionairesMM Profile' : 'Scan to Save'}
          </p>
          <div className="p-3 rounded-xl bg-white" data-testid="card-qr">
            <QRCodeSVG value={qrTarget} size={140} level="M" />
          </div>
          <p className="mt-3 text-[11px] text-white/40 break-all text-center max-w-xs" data-testid="card-qr-target">{qrTarget}</p>
        </div>
      )}

      <footer className="max-w-md mx-auto px-6 py-6 text-center">
        <Link to="/" className="text-xs uppercase tracking-[0.3em] text-white/40 hover:text-brand-gold">
          Powered by WeMasterminds
        </Link>
      </footer>
    </div>
  );
}
